import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { UsersService } from '../../services/users/Users';
import { NetbirdPeer, NetbirdPeers } from '../../interfaces/Netbird';

const service = new UsersService();

interface NetbirdPeersStatus {
  true: number;
  false: number;
  approvalPending: number;
  loginExpired: number;
}

interface NetbirdPeersState {
  peers: NetbirdPeers;
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
  vpnPeersStatus: NetbirdPeersStatus | null;
  vpnOsPeers: Record<string, number> | null;
  count: number;
}

const initialState: NetbirdPeersState = {
  peers: [],
  status: 'idle',
  error: null,
  vpnPeersStatus: null,
  vpnOsPeers: null,
  count: 0,
};

export const fetchVpnPeers = createAsyncThunk(
  'netbirdPeers/fetchVpnPeers',
  async (companyId: number, { rejectWithValue }) => {
    try {
      const response = await service.getVpnPeers(companyId);
      return response.peers || [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const approvePeerVpn = createAsyncThunk(
  'netbirdPeers/approvePeerVpn',
  async (
    { peerId, companyId }: { peerId: string; companyId: number },
    { rejectWithValue }
  ) => {
    try {
      // Note: You may need to implement this in UsersService if it doesn't exist
      // For now, we'll assume the service has this method
      // await service.approveVpnPeer(peerId, companyId);
      return peerId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const handlePeerStatus = (peers: NetbirdPeer[]) => {
  return peers.reduce(
    (acc, peer) => {
      if (peer.connected) acc.true++;
      else acc.false++;
      if (peer.approval_required) acc.approvalPending++;
      if (peer.login_expired) acc.loginExpired++;
      return acc;
    },
    {
      true: 0,
      false: 0,
      approvalPending: 0,
      loginExpired: 0,
    }
  );
};

const handleOsDistribution = (peers: NetbirdPeers) => {
  const os = peers.map((item: NetbirdPeer) => item.os);

  return os.reduce<Record<string, number>>((acc, curr) => {
    return (acc[curr] = (acc[curr] || 0) + 1), acc;
  }, {});
};

const netbirdPeersSlice = createSlice({
  name: 'netbirdPeers',
  initialState,
  reducers: {
    setVpnPeers: (state, action: PayloadAction<NetbirdPeers>) => {
      state.peers = action.payload;
      state.count = action.payload.length;
      state.vpnPeersStatus = handlePeerStatus(action.payload);
      state.vpnOsPeers = handleOsDistribution(action.payload);
    },
    updateVpnPeer: (state, action: PayloadAction<NetbirdPeer>) => {
      const index = state.peers.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.peers[index] = action.payload;
        state.vpnPeersStatus = handlePeerStatus(state.peers);
        state.vpnOsPeers = handleOsDistribution(state.peers);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVpnPeers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(
        fetchVpnPeers.fulfilled,
        (state, action: PayloadAction<NetbirdPeers>) => {
          state.status = 'idle';
          state.peers = action.payload;
          state.count = action.payload.length;
          state.vpnPeersStatus = handlePeerStatus(action.payload);
          state.vpnOsPeers = handleOsDistribution(action.payload);
        }
      )
      .addCase(fetchVpnPeers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(approvePeerVpn.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approvePeerVpn.fulfilled, (state, action) => {
        state.status = 'idle';
        const peer = state.peers.find((p) => p.id === action.payload);
        if (peer) {
          peer.approval_required = false;
          state.vpnPeersStatus = handlePeerStatus(state.peers);
        }
      })
      .addCase(approvePeerVpn.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const { setVpnPeers, updateVpnPeer, clearError } =
  netbirdPeersSlice.actions;
export default netbirdPeersSlice.reducer;
